import HttpService from "./HttpService";
const API_ENDPOINT = "/division";

const isFile = (v) => typeof File !== "undefined" && v instanceof File;
const isFileList = (v) => typeof FileList !== "undefined" && v instanceof FileList;

// Builds a multipart FormData body for the division create/update endpoints.
// Handles plain scalars, a `logo` / `letter_head_image` file (File or FileList),
// the nested `letter_head_settings` object, and the repeatable
// `authorized_signatures` array (each row may carry an `image` File).
const buildDivisionFormData = (values = {}) => {
  const fd = new FormData();

  const appendFile = (key, val) => {
    if (isFileList(val)) {
      if (val.length) fd.append(key, val[0]);
    } else if (isFile(val)) {
      fd.append(key, val);
    }
  };

  Object.keys(values).forEach((key) => {
    const val = values[key];
    if (val === undefined || val === null) return;

    if (key === "logo" || key === "letter_head_image") {
      appendFile(key, val);
      return;
    }

    if (key === "letter_head_settings" && typeof val === "object") {
      Object.keys(val).forEach((sub) => {
        if (val[sub] !== undefined && val[sub] !== null && val[sub] !== "") {
          fd.append(`letter_head_settings[${sub}]`, val[sub]);
        }
      });
      return;
    }

    if (key === "authorized_signatures" && Array.isArray(val)) {
      val.forEach((row, i) => {
        if (row.id !== undefined && row.id !== null && row.id !== "") {
          fd.append(`authorized_signatures[${i}][id]`, row.id);
        }
        appendFile(`authorized_signatures[${i}][image]`, row.image);
        if (row.person_name !== undefined && row.person_name !== null) {
          fd.append(`authorized_signatures[${i}][person_name]`, row.person_name);
        }
        if (row.designation !== undefined && row.designation !== null) {
          fd.append(`authorized_signatures[${i}][designation]`, row.designation);
        }
        fd.append(
          `authorized_signatures[${i}][is_active]`,
          row.is_active ? 1 : 0
        );
        fd.append(
          `authorized_signatures[${i}][is_default]`,
          row.is_default ? 1 : 0
        );
      });
      return;
    }

    if (isFileList(val) || isFile(val)) {
      appendFile(key, val);
      return;
    }

    fd.append(key, val);
  });

  return fd;
};

const LETTERHEAD_FONTS_URL = "/letter-management/letterhead-fonts";

// Only the three DomPDF core keys, so a failed catalog fetch can still offer
// choices the backend will accept. Any richer list would risk a 400 on save.
const FALLBACK_FONTS = [
  { key: "helvetica", label: "Helvetica / Arial", css_stack: "Helvetica, Arial, sans-serif", source: "core" },
  { key: "times", label: "Times", css_stack: '"Times New Roman", Times, serif', source: "core" },
  { key: "courier", label: "Courier", css_stack: '"Courier New", Courier, monospace', source: "core" },
];

const DivisionService = {
  async getStateCities(stateId, countryId) {
    try {
      const response = await HttpService.get(
        `/city-get/${stateId}/${countryId}`
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching state cities:", error);
      throw error;
    }
  },
  async getDivisions() {
    try {
      const response = await HttpService.get(`${API_ENDPOINT}`);
      return response.data;
    } catch (error) {
      console.error("Error fetching divisions:", error);
      throw error;
    }
  },
  async getDivisionPaginated(pageConfig) {
    const pageConfigParams = new URLSearchParams(pageConfig);
    try {
      const response = await HttpService.get(
        `${API_ENDPOINT}-paginated?${pageConfigParams}`
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching divisions:", error);
      throw error;
    }
  },
  async getDivisionById(divisionId) {
    try {
      const response = await HttpService.get(
        `${API_ENDPOINT}/${divisionId}/edit`
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching division:", error);
      throw error;
    }
  },
  // Teams, campaigns, projects, departments and sections belonging to a division.
  async getDivisionWiseData(divisionId) {
    try {
      const response = await HttpService.get(`/division-wise-data/${divisionId}`);
      return response.data;
    } catch (error) {
      console.error("Error fetching division wise data:", error);
      throw error;
    }
  },
  async addDivision(data) {
    try {
      const response = await HttpService.post(
        API_ENDPOINT,
        buildDivisionFormData(data),
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error adding division:", error);
      throw error;
    }
  },
  async updateDivision(divisionId, data) {
    try {
      const fd = buildDivisionFormData(data);
      fd.append("_method", "PUT");
      const response = await HttpService.post(
        `${API_ENDPOINT}/${divisionId}`,
        fd,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error updating division:", error);
      throw error;
    }
  },
  async deleteDivision(cityId) {
    try {
      const response = await HttpService.delete(`${API_ENDPOINT}/${cityId}`);
      return response.data;
    } catch (error) {
      console.error("Error deleting division:", error);
      throw error;
    }
  },
  // Letterhead font catalog. Rows are { key, label, css_stack, source }; `key`
  // is what gets submitted as letter_head_settings[font_family] — never the
  // label or the CSS stack, the backend validates against the catalog keys.
  async fetchLetterheadFonts() {
    try {
      const response = await HttpService.get(LETTERHEAD_FONTS_URL);
      const fonts = response.data?.data;
      if (Array.isArray(fonts) && fonts.length) return fonts;
      return FALLBACK_FONTS;
    } catch (error) {
      console.error("Error fetching letterhead fonts:", error);
      return FALLBACK_FONTS;
    }
  },
  // Public (unauthenticated) stylesheet carrying @font-face for every hosted
  // family, so the picker can render each option in its own typeface.
  letterheadFontsCssUrl() {
    const base = (HttpService.defaults.baseURL || "").replace(/\/+$/, "");
    return `${base}${LETTERHEAD_FONTS_URL}.css`;
  },
};

export default DivisionService;
